import type { ShoppingList } from '@recipes/contracts';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Switch,
} from '@recipes/ui';
import { useMutation } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { HouseholdSelect } from '@/features/shared/household-select';
import { useHouseholds } from '@/features/shared/hooks';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth/auth-provider';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import { toastError } from '@/lib/errors';

/**
 * Share a list with the household – also an existing one (docs/partial/shopping-list-extensions-spec.md
 * 5.1). One switch; it uses the household the list belongs to, else the user's only household,
 * else asks which one. Only the owner decides who sees the list. Sharing needs a connection.
 */
export function ShareDialog({
  open,
  onOpenChange,
  list,
  onChanged,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  list: ShoppingList;
  onChanged: (list: ShoppingList) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const { user } = useAuth();
  const online = useOnlineStatus();
  const { data: households } = useHouseholds();
  const [chosen, setChosen] = useState<string | null>(null);
  const isOwner = user?.id === list.ownerId;
  const shared = Boolean(list.householdId);
  const current = households?.find((h) => h.id === list.householdId);
  const target =
    list.householdId ?? chosen ?? (households?.length === 1 ? households[0]!.id : null);

  const mutation = useMutation({
    mutationFn: (householdId: string | null) =>
      api.shopping.update(list.id, { householdId, version: list.version }),
    onSuccess: (updated) => onChanged(updated),
    onError: (e) => toastError(e),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('common.close')} data-testid="shopping-share-dialog">
        <DialogHeader>
          <DialogTitle>{t('shopping.share.title')}</DialogTitle>
          <DialogDescription>
            {shared
              ? t('shopping.share.stateHousehold', {
                  name: current?.name ?? t('shared.household.shared'),
                })
              : t('shopping.share.statePersonal')}
          </DialogDescription>
        </DialogHeader>

        {!households?.length ? (
          <Alert>
            {t('shopping.share.noHousehold')}{' '}
            <Link to="/households" className="underline">
              {t('shopping.share.toHouseholds')}
            </Link>
          </Alert>
        ) : (
          <div className="space-y-4">
            {!shared && households.length > 1 && (
              <HouseholdSelect
                value={target}
                onChange={setChosen}
                includePersonal={false}
                label={t('shopping.share.which')}
              />
            )}
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor={id} className="flex-1">
                {t('shopping.share.withHousehold')}
                <span className="block text-xs font-normal text-muted-foreground">
                  {t('shopping.share.visibility')}
                </span>
              </Label>
              <Switch
                id={id}
                checked={shared}
                disabled={!isOwner || !online || mutation.isPending || (!shared && !target)}
                onCheckedChange={(on) => mutation.mutate(on ? target : null)}
                data-testid="shopping-share-switch"
              />
            </div>
            {!isOwner && (
              <p className="text-sm text-muted-foreground">{t('shopping.share.ownerOnly')}</p>
            )}
            {!online && (
              <p className="text-sm text-muted-foreground">{t('shopping.share.needsOnline')}</p>
            )}
            <p className="text-sm text-muted-foreground">
              {t('shopping.share.inviteHint')}{' '}
              <Link to="/households" className="underline">
                {t('shopping.share.toHouseholds')}
              </Link>
            </p>
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
